import {useQuery} from "@tanstack/react-query";
import {useEffect, useRef, useState} from "react";
import {useSearchParams} from "react-router";
import {Template} from "../../../components/template/template.jsx";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import {Card} from "../../../components/card/card.jsx";
import Content from "./components/content.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import PrintOptionsModal from "../../../components/print-options-modal/print-options-modal.jsx";
import solicitationService from "../../../services/web/solicitation-service.js";
import projectUtils from "../../../utils/project-utils.js";
import ReportUtils from "../../../utils/report-utils.js";
import natureService from "../../../services/web/nature-service.js";

const printFields = [
    {
        title: "Anexos",
        options: [
            {
                key: "showImages",
                label: "Exibir imagens na impressão",
            }
        ],
    },
];

const initialPrintOptions = Object.fromEntries(
    printFields.flatMap((group) =>
        group.options.map((item) => [item.key, item.key === "showImages"])
    )
);

const SolicitationInfo = () => {
    const [headerSelected, setHeaderSelected] = useState(-1);
    const [isPrinting, setIsPrinting] = useState(false);

    const [printModal, setPrintModal] = useState(false);
    const [printOptions, setPrintOptions] = useState(initialPrintOptions);
    const printComponentRef = useRef(null);

    const title = "Solicitações";

    const [searchParams] = useSearchParams();
    const encodedFilters = searchParams.get("f");

    const filters = encodedFilters ? JSON.parse(decodeURIComponent(atob(encodedFilters))) : {};

    const appliedFilters = [
        { field: "dateBetween", type: "between", value: {dtIni: filters.dtStart || "", dtEnd: filters.dtEnd || ""} },
        { field: "status", type: "like", value: filters.status },
        { field: "nature", type: "like", value: filters.nature },
        { field: "description", type: "like", value: filters.description }
    ].filter((item) => {
        if (item.type === "between") return item.value?.dtIni || item.value?.dtEnd;
        return (item.value !== null && item.value !== undefined && item.value !== "");
    });

    const filterLabels = {
        dtPeriod: "Data/Hora do fato entre",
        status: "Status",
        nature: "Naturezas",
        description: "Descrição"
    };

    const { data: reportData, isLoading: isLoadingReport } = useQuery({
        queryFn: () => solicitationService.list(appliedFilters),
        queryKey: ["list", {appliedFilters}],
    });

    const { data: reasons } = useQuery({
        queryFn: () => natureService.getAllToSelect(),
        queryKey: ["reasons"]
    });

    const filteredNature = !filters.nature || !reasons ? [] : reasons.filter(item => filters.nature.split(",").includes(String(item.value))).map(item => item.label);

    useEffect(() => {
        ReportUtils.appendFilters({
            [filterLabels.dtPeriod]: `${filters.dtStart ? projectUtils.formatDate(filters.dtStart) : ''}${filters.dtEnd ? `${filters.dtStart ? ' à ' : ''}${projectUtils.formatDate(filters.dtEnd)}` : ''}`,
            [filterLabels.status]: filters.statusLabel ?? '',
            [filterLabels.nature]: filteredNature,
            [filterLabels.description]: filters.description ?? ''
        });
    }, [reportData, reasons]);

    const printItems = printFields;
    const hasData = reportData?.length > 0;
    const hasFilters = appliedFilters.some((filter) => {
        if (filter.type === "between") return filter.value?.dtIni ?? filter.value?.dtEnd;
        return (filter.value !== null && filter.value !== undefined && filter.value !== "");
    });

    return (
        <>
            <Template isLoading={isLoadingReport || isPrinting}>
                <Template.Header>
                    <Template.Title>{title}</Template.Title>
                    <HeaderButtons
                        printOptions={true}
                        printable={true}
                        setPrintModal={setPrintModal}
                        printComponentRef={printComponentRef}
                        reportData={reportData}
                        data={hasData}
                        headerChange={true}
                        headerSelected={headerSelected}
                        setHeaderSelected={setHeaderSelected}
                    />
                </Template.Header>
                <Template.Body className="pl-0!">
                    <Card className="w-full max-w-[1030px] mx-auto p-[0]!">
                        <Card.Body>
                            <NoDataContent reportData={reportData} data={hasData}/>
                            <Content
                                reportData={reportData}
                                data={hasData}
                                filters={hasFilters}
                                entity={headerSelected}
                                title={title}
                                printComponentRef={printComponentRef}
                                setIsPrinting={setIsPrinting}
                                printOptions={printOptions}
                            />
                        </Card.Body>
                    </Card>
                </Template.Body>
            </Template>

            <PrintOptionsModal
                open={printModal}
                setOpen={setPrintModal}
                printRef={printComponentRef}
                options={printOptions}
                setOptions={setPrintOptions}
                items={printItems}
            />
        </>
    )
};

export default SolicitationInfo;